import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssistantProviderProps, AssistantContextValue, AssistantAssets, TranslationOverrides } from '../types';

// Import default assets
import payoutsAgentIconUrl from '../assets/png/payouts-agent-icon.png';
import payoutsAgentBannerUrl from '../assets/png/payouts-agent-long.png';
import insightsAgentIconUrl from '../assets/png/insights-agent-icon.png';
import insightsAgentBannerUrl from '../assets/png/insights-agent-long.png';
import formulasAgentIconUrl from '../assets/png/formulas-agent-icon.png';
import formulasAgentBannerUrl from '../assets/png/formulas-agent-long.png';
import suiteAgentIconUrl from '../assets/png/suite-agent-icon.png';
import planDocsAgentBannerUrl from '../assets/png/plan-docs-agent-long.png';
import planDocsAgentsIconUrl from '../assets/png/plan-docs-agent-icon.png';

// Import default translations and i18n instance
import assistantTranslations from '../translations';
import { updateAssistantTranslations, i18n } from '../i18n';

// Default asset map
const defaultAssets: AssistantAssets = {
  payoutsAgentIconUrl,
  payoutsAgentBannerUrl,
  insightsAgentIconUrl,
  insightsAgentBannerUrl,
  formulasAgentIconUrl,
  formulasAgentBannerUrl,
  suiteAgentIconUrl,
  planDocsAgentBannerUrl,
  planDocsAgentsIconUrl,
};

// Default translations
const defaultTranslations: TranslationOverrides = assistantTranslations;

// Create context
const AssistantContext = createContext<AssistantContextValue | null>(null);

/**
 * AssistantProvider - Self-contained provider with override capabilities
 *
 * @example
 * ```tsx
 * <AssistantProvider
 *   assetsOverride={{ planDocIcon: customUrl }}
 *   translationsOverride={{ "en-US": { assistant: { "welcome": "Custom welcome!" } } }}
 * >
 *   <AIShellWrapper />
 * </AssistantProvider>
 * ```
 */
export const AssistantProvider: React.FC<AssistantProviderProps> = ({
  children,
  assetsOverride = {},
  translationsOverride = {},
}) => {
  // Update the assistant i18n instance when translation overrides change
  useEffect(() => {
    if (Object.keys(translationsOverride).length > 0) {
      updateAssistantTranslations(translationsOverride);
    }
  }, [translationsOverride]);

  const value = useMemo((): AssistantContextValue => {
    // Merge default assets with overrides
    const mergedAssets: AssistantAssets = {
      ...defaultAssets,
      ...assetsOverride,
    };

    // Deep merge translations with overrides
    const mergedTranslations: TranslationOverrides = {};

    // Start with defaults
    Object.keys(defaultTranslations).forEach((lang) => {
      mergedTranslations[lang] = { ...defaultTranslations[lang] };
    });

    // Apply overrides (deep merge per language)
    Object.keys(translationsOverride).forEach((lang) => {
      if (mergedTranslations[lang]) {
        mergedTranslations[lang] = {
          ...mergedTranslations[lang],
          ...translationsOverride[lang],
        };
      } else {
        mergedTranslations[lang] = { ...translationsOverride[lang] };
      }
    });

    return {
      assets: mergedAssets,
      translations: mergedTranslations,
    };
  }, [assetsOverride, translationsOverride]);

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
};

/**
 * Hook to access asset overrides
 */
export const useAssistantAssets = (): AssistantAssets => {
  const context = useContext(AssistantContext);

  if (!context) {
    // Fallback to default assets if no provider
    return defaultAssets;
  }

  return context.assets;
};

/**
 * Hook to access translation overrides
 */
export const useAssistantTranslations = (): TranslationOverrides => {
  const context = useContext(AssistantContext);

  if (!context) {
    // Fallback to default translations if no provider
    return defaultTranslations;
  }

  return context.translations;
};

/**
 * Hook to access full context
 */
export const useAssistantContext = (): AssistantContextValue => {
  const context = useContext(AssistantContext);

  if (!context) {
    // Return defaults if no provider
    return {
      assets: defaultAssets,
      translations: defaultTranslations,
    };
  }

  return context;
};

/**
 * Hook to use assistant-specific translations with react-i18next
 *
 * This uses the dedicated assistant i18n instance, so it works independently
 * of the consuming app's i18n setup.
 *
 * @example
 * ```tsx
 * const { t } = useAssistantTranslation();
 * return <div>{t('assistant:browser_welcome')}</div>;
 * ```
 */
export const useAssistantTranslation = () => {
  return useTranslation('assistant', { i18n });
};
